// D3 — Análisis Mensual del directivo.
//
// Review estratégico mensual: KPIs del mes vs anterior, tendencia de cumplimiento
// 6 meses por planta (multi-línea, planta foco resaltada) y capacidad utilizada
// estacional 12 meses con banda de temporada alta. SOLO LECTURA.
// Margen operativo = utilidad de LÍNEAS en $ (WS-5 B4b), gateado por
// puede_ver_costos. Export diferido (botón deshabilitado).

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DirectivoAnalisisMensual, MetricaSerie } from 'schemas';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '../../components/ui/card';
import { Icon } from '../../components/ui/icon';
import { KpiCard } from '../../components/ui/kpi-card';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Spinner } from '../../components/ui/spinner';
import { DirectivoLayout } from '../../components/layout/DirectivoLayout';
import { useDirectivoAnalisisMensual } from '../../lib/api/queries';
import { PeriodoNav, primerDiaMesActual, shiftMeses } from './components/PeriodoNav';
import { leerMesParam } from './lib/urlParams';
import { hayPendiente, sumarPendientes, textoPendiente } from './lib/pendiente';

function miles(n: number): string {
  return new Intl.NumberFormat('es-CO').format(Math.round(n));
}

// Pesos compactos para KPIs: $12,3 M / $450 k / $980.
function pesosCortos(n: number): string {
  const abs = Math.abs(n);
  const signo = n < 0 ? '-' : '';
  if (abs >= 1_000_000)
    return `${signo}$${(abs / 1_000_000).toLocaleString('es-CO', { maximumFractionDigits: 1 })} M`;
  if (abs >= 1_000)
    return `${signo}$${(abs / 1_000).toLocaleString('es-CO', { maximumFractionDigits: 0 })} k`;
  return `${signo}$${miles(abs)}`;
}

const METRICA_LABEL: Record<MetricaSerie, string> = {
  cumplimiento: 'Cumplimiento',
  produccion: 'Producción',
  paros: 'Paros',
};

export function D3AnalisisMensualPage() {
  // El mes vive en la URL (?mes=YYYY-MM-DD, o YYYY-MM → día 1): refrescar /
  // atrás lo conservan. Ausente o inválido → mes en curso (null).
  const [searchParams, setSearchParams] = useSearchParams();
  const mes = leerMesParam(searchParams.get('mes'));
  const setMes = (m: string | null) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (m) next.set('mes', m);
        else next.delete('mes');
        return next;
      },
      { replace: true },
    );
  const { data, isLoading, isError } = useDirectivoAnalisisMensual(mes);
  const [metrica, setMetrica] = useState<MetricaSerie>('cumplimiento');

  if (isLoading || !data) {
    return (
      <DirectivoLayout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Spinner />
        </div>
      </DirectivoLayout>
    );
  }
  if (isError) {
    return (
      <DirectivoLayout>
        <div className="flex min-h-[60vh] items-center justify-center text-sm text-crit">
          No se pudo cargar el análisis mensual.
        </div>
      </DirectivoLayout>
    );
  }

  return (
    <DirectivoLayout>
      <div className="mx-auto flex max-w-[1280px] flex-col gap-5">
        <Header data={data} />
        <Filtros data={data} metrica={metrica} onMetrica={setMetrica} onMes={setMes} />
        <KpiRow data={data} />
        <TendenciaChart data={data} metrica={metrica} />
        <RentabilidadTiendasSection data={data} />
        <EstacionalChart data={data} />
      </div>
    </DirectivoLayout>
  );
}

function Header({ data }: { data: DirectivoAnalisisMensual }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h1 className="text-[24px] font-medium leading-tight text-fg">Análisis Mensual</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          {data.mes.label} · {data.mes.dias_con_datos} días con datos · {data.mes.plantas_total}{' '}
          plantas
        </p>
      </div>
      <button
        type="button"
        disabled
        title="Exportar — disponible en F1.5"
        className="inline-flex cursor-not-allowed items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-medium text-fg-secondary opacity-60"
      >
        <Icon name="download" size={14} stroke={2} />
        Exportar
      </button>
    </div>
  );
}

function Filtros({
  data,
  metrica,
  onMetrica,
  onMes,
}: {
  data: DirectivoAnalisisMensual;
  metrica: MetricaSerie;
  onMetrica: (m: MetricaSerie) => void;
  onMes: (mes: string | null) => void;
}) {
  const isCurrent = data.mes.desde >= primerDiaMesActual();
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 rounded-md border border-border bg-surface-elevated p-3">
      <div className="flex flex-wrap items-center gap-5">
        <div className="flex flex-col gap-1">
          <span className="text-[9px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            Mes
          </span>
          <PeriodoNav
            label={data.mes.label}
            isCurrent={isCurrent}
            canNext={!isCurrent}
            onPrev={() => onMes(shiftMeses(data.mes.desde, -1))}
            onNext={() => onMes(shiftMeses(data.mes.desde, 1))}
            onReset={() => onMes(null)}
          />
        </div>
        <Filtro label="Planta" valor={`Todas (${data.mes.plantas_total})`} />
        <Filtro
          label="Cliente"
          valor={data.kpis.cumplimiento_mes.valor_pct !== null ? 'Todos' : '—'}
        />
      </div>
      <SegmentedControl
        value={metrica}
        onChange={onMetrica}
        options={[
          { value: 'cumplimiento', label: 'Cumplimiento' },
          { value: 'produccion', label: 'Producción' },
          { value: 'paros', label: 'Paros' },
        ]}
      />
    </div>
  );
}

function Filtro({ label, valor }: { label: string; valor: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[9px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        {label}
      </span>
      <span className="text-xs text-fg">{valor}</span>
    </div>
  );
}

function KpiRow({ data }: { data: DirectivoAnalisisMensual }) {
  const k = data.kpis;
  const delta = k.cumplimiento_mes.delta_pp;
  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-border bg-border lg:grid-cols-4">
      <div className="bg-surface-elevated py-4">
        <KpiCard
          label="Cumplimiento del mes"
          value={k.cumplimiento_mes.valor_pct !== null ? k.cumplimiento_mes.valor_pct : '—'}
          valueSuffix={k.cumplimiento_mes.valor_pct !== null ? '%' : undefined}
          trend={
            delta === null
              ? undefined
              : {
                  direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
                  text: `${delta > 0 ? '+' : ''}${delta} vs ${k.cumplimiento_mes.mes_anterior_label}`,
                  tone: delta >= 0 ? 'ok' : 'crit',
                }
          }
        />
      </div>
      <div className="bg-surface-elevated py-4">
        <KpiCard
          label="Producción total"
          value={miles(k.produccion_total.cantidad)}
          valueSuffix="paq"
          sub={
            k.produccion_total.meta > 0 ? `vs ${miles(k.produccion_total.meta)} meta` : undefined
          }
        />
      </div>
      <div className="bg-surface-elevated py-4">
        <KpiCard
          label="Capacidad utilizada"
          value={k.capacidad_utilizada.valor_pct !== null ? k.capacidad_utilizada.valor_pct : '—'}
          valueSuffix={k.capacidad_utilizada.valor_pct !== null ? '%' : undefined}
          sub={
            k.capacidad_utilizada.promedio_q1_pct !== null
              ? `vs ${Math.round(k.capacidad_utilizada.promedio_q1_pct)}% promedio Q1`
              : undefined
          }
        />
      </div>
      <div className="bg-surface-elevated py-4">
        <MargenOperativoCard data={data} />
      </div>
    </div>
  );
}

function MargenOperativoCard({ data }: { data: DirectivoAnalisisMensual }) {
  if (!data.puede_ver_costos) {
    return <KpiCard label="Margen operativo" value="—" sub="Sin permiso para ver costos" />;
  }
  const r = data.rentabilidad;
  // DN10: el margen va sobre producción VALIDADA; lo capturado sin validar
  // (líneas + tiendas) se declara aquí y no suma.
  const pend = sumarPendientes(
    r?.pendiente_validacion_utilidad,
    data.rentabilidad_tiendas?.pendiente_validacion,
  );
  const pendiente = (
    <p
      className={`mt-2 px-5 text-[11px] ${hayPendiente(pend) ? 'text-warn' : 'text-fg-tertiary'}`}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {textoPendiente(pend, pesosCortos(pend.valor_producido))}
    </p>
  );
  if (!r || r.utilidad_pct === null) {
    return (
      <>
        <KpiCard
          label="Margen operativo"
          hint="Utilidad ÷ valor producido sobre producción validada por supervisión (misma base del informe gerencial)."
          value="—"
          sub="Sin costos/precios cargados en lo validado"
        />
        {pendiente}
      </>
    );
  }
  return (
    <>
      <KpiCard
        label="Margen operativo"
        hint="Utilidad ÷ valor producido sobre producción validada por supervisión, la misma base del informe gerencial del coordinador. Consolida líneas y tiendas; lo pendiente de validar se declara debajo y no suma."
        value={data.kpis.margen_operativo_pct ?? r.utilidad_pct}
        valueSuffix="%"
        sub={
          data.rentabilidad_tiendas
            ? `Líneas ${pesosCortos(r.utilidad)} · Tiendas ${pesosCortos(data.rentabilidad_tiendas.utilidad)}`
            : `${pesosCortos(r.utilidad)} util.${r.solo_lineas ? ' · solo líneas' : ''}`
        }
        trend={{
          direction: r.utilidad >= 0 ? 'up' : 'down',
          text: `${pesosCortos(r.valor_producido)} facturado · producción validada`,
          tone: r.utilidad >= 0 ? 'ok' : 'crit',
        }}
      />
      {pendiente}
    </>
  );
}

const SERIE_KEY: Record<MetricaSerie, 'cumplimiento_pct' | 'produccion' | 'paros_horas'> = {
  cumplimiento: 'cumplimiento_pct',
  produccion: 'produccion',
  paros: 'paros_horas',
};

function RentabilidadTiendasSection({ data }: { data: DirectivoAnalisisMensual }) {
  const t = data.rentabilidad_tiendas;
  if (!data.puede_ver_costos || !t) return null;
  const pesos = (v: number) =>
    `$${Math.round(v).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`;
  return (
    <section className="rounded-xl border border-border-subtle bg-surface-elevated p-4">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold">Rentabilidad de tiendas</h2>
        <p className="text-xs text-fg-tertiary">
          Reempaque por destino · producción validada · los insumos (sticker y termo) se cobran
          adicional al cliente
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-border-subtle text-left text-xs uppercase tracking-wide text-fg-tertiary">
              <th className="py-2 pr-3">Destino</th>
              <th className="py-2 pr-3 text-right">Valor unidades</th>
              <th className="py-2 pr-3 text-right">Insumos cobrados</th>
              <th className="py-2 pr-3 text-right">Valor de venta</th>
              <th className="py-2 pr-3 text-right">Costo mano de obra</th>
              <th className="py-2 pr-3 text-right">Utilidad</th>
              <th className="py-2 text-right">% Utilidad</th>
            </tr>
          </thead>
          <tbody>
            {t.destinos.map((d) => (
              <tr key={d.tienda_id} className="border-b border-border-subtle/60">
                <td className="py-2 pr-3">{d.tienda_nombre}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{pesos(d.valor_unidades)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{pesos(d.ingreso_insumos)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{pesos(d.valor_producido)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{pesos(d.costo_mano_obra)}</td>
                <td
                  className={`py-2 pr-3 text-right tabular-nums ${d.utilidad < 0 ? 'text-danger' : ''}`}
                >
                  {pesos(d.utilidad)}
                </td>
                <td
                  className={`py-2 text-right tabular-nums ${(d.utilidad_pct ?? 0) < 0 ? 'text-danger' : ''}`}
                >
                  {d.utilidad_pct !== null ? `${d.utilidad_pct.toFixed(1)}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-medium">
              <td className="py-2 pr-3">Total tiendas</td>
              <td className="py-2 pr-3 text-right tabular-nums">{pesos(t.valor_unidades)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{pesos(t.ingreso_insumos)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{pesos(t.valor_producido)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{pesos(t.costo_mano_obra)}</td>
              <td
                className={`py-2 pr-3 text-right tabular-nums ${t.utilidad < 0 ? 'text-danger' : ''}`}
              >
                {pesos(t.utilidad)}
              </td>
              <td
                className={`py-2 text-right tabular-nums ${(t.utilidad_pct ?? 0) < 0 ? 'text-danger' : ''}`}
              >
                {t.utilidad_pct !== null ? `${t.utilidad_pct.toFixed(1)}%` : '—'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {/* DN10: corridas capturadas sin validar — declaradas, no suman. */}
      <p
        className={`mt-2 text-[11px] ${
          hayPendiente(t.pendiente_validacion) ? 'text-warn' : 'text-fg-tertiary'
        }`}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {textoPendiente(
          t.pendiente_validacion,
          pesos(t.pendiente_validacion?.valor_producido ?? 0),
        )}
      </p>
    </section>
  );
}

function TendenciaChart({
  data,
  metrica,
}: {
  data: DirectivoAnalisisMensual;
  metrica: MetricaSerie;
}) {
  const key = SERIE_KEY[metrica];
  // Pivot: una fila por mes, una columna por planta.
  const chartData = useMemo(() => {
    return data.tendencia.meses.map((m) => {
      const row: Record<string, number | string | null> = { mes: m.label };
      for (const pl of data.tendencia.plantas) {
        const punto = data.tendencia.puntos.find(
          (p) => p.mes_iso === m.mes_iso && p.planta_id === pl.planta_id,
        );
        row[pl.planta_id] = punto ? (punto[key] as number | null) : null;
      }
      return row;
    });
  }, [data, key]);

  const foco = data.tendencia.plantas.find((p) => p.es_foco);
  const sufijo = metrica === 'cumplimiento' ? '%' : metrica === 'paros' ? 'h' : ' paq';

  return (
    <Card className="p-4">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-medium text-fg">
          Tendencia de {METRICA_LABEL[metrica].toLowerCase()} · 6 meses por planta
        </h2>
      </div>
      <p className="mb-3 text-xs text-fg-tertiary">
        {data.tendencia.meses[0]?.label} – {data.tendencia.meses.at(-1)?.label}
        {foco ? ` · ${foco.nombre} destacado (planta foco)` : ''}
      </p>
      <div className="h-[260px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 64, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="mes"
              tick={{ fontSize: 11, fill: 'var(--color-fg-secondary)' }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
            />
            <YAxis
              domain={metrica === 'cumplimiento' ? [60, 100] : ['auto', 'auto']}
              tickFormatter={(v) => (metrica === 'cumplimiento' ? `${v}%` : miles(Number(v)))}
              tick={{ fontSize: 11, fill: 'var(--color-fg-tertiary)' }}
              axisLine={false}
              tickLine={false}
              width={48}
            />
            <Tooltip
              contentStyle={{
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(v, _n, item) => [
                v === null ? 's/d' : `${miles(Number(v))}${sufijo}`,
                data.tendencia.plantas.find((p) => p.planta_id === item?.dataKey)?.nombre ?? '',
              ]}
            />
            {data.tendencia.plantas.map((pl) => (
              <Line
                key={pl.planta_id}
                type="monotone"
                dataKey={pl.planta_id}
                name={pl.nombre}
                stroke={pl.es_foco ? 'var(--color-accent)' : 'var(--color-fg-tertiary)'}
                strokeWidth={pl.es_foco ? 2.5 : 1.5}
                strokeOpacity={pl.es_foco ? 1 : 0.55}
                dot={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function EstacionalChart({ data }: { data: DirectivoAnalisisMensual }) {
  const chartData = data.estacional.meses.map((m) => ({
    mes: m.label,
    capacidad: data.estacional.puntos.find((p) => p.mes_iso === m.mes_iso)?.capacidad_pct ?? null,
  }));
  // Rango contiguo de temporada alta (primer y último mes marcado presente).
  const altaLabels = data.estacional.meses
    .filter((m) => data.estacional.temporada_alta_meses.includes(m.mes_iso))
    .map((m) => m.label);
  const bandX1 = altaLabels[0];
  const bandX2 = altaLabels.at(-1);

  return (
    <Card className="p-4">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-medium text-fg">
          Capacidad utilizada · análisis estacional 12 meses
        </h2>
        {data.estacional.promedio_pct !== null ? (
          <span className="text-xs text-fg-tertiary">
            Promedio 12 meses:{' '}
            <span className="font-medium text-fg">{Math.round(data.estacional.promedio_pct)}%</span>
          </span>
        ) : null}
      </div>
      <p className="mb-3 text-xs text-fg-tertiary">
        {data.mes.plantas_total} plantas · temporada alta destacada
      </p>
      <div className="h-[240px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            {bandX1 && bandX2 ? (
              <ReferenceArea
                x1={bandX1}
                x2={bandX2}
                fill="var(--color-accent)"
                fillOpacity={0.07}
                label={{
                  value: 'TEMPORADA ALTA',
                  position: 'insideTop',
                  fontSize: 9,
                  fill: 'var(--color-fg-tertiary)',
                }}
              />
            ) : null}
            <XAxis
              dataKey="mes"
              tick={{ fontSize: 11, fill: 'var(--color-fg-secondary)' }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
            />
            <YAxis
              domain={[0, 100]}
              tickFormatter={(v) => `${v}%`}
              tick={{ fontSize: 11, fill: 'var(--color-fg-tertiary)' }}
              axisLine={false}
              tickLine={false}
              width={48}
            />
            <Tooltip
              contentStyle={{
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(v) => [v === null ? 's/d' : `${Math.round(Number(v))}%`, 'Capacidad']}
            />
            <Line
              type="monotone"
              dataKey="capacidad"
              stroke="var(--color-accent)"
              strokeWidth={2}
              dot={{ r: 2, fill: 'var(--color-accent)' }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
